import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Headset, Loader2, Plus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { cn } from '@/lib/utils/cn';
import { AGENT_TYPE_LABEL, type AgentRow, type AgentType } from '../types';

interface Props {
  mode: 'create' | 'update';
  initialGroupName: string;
  initialAgents: AgentRow[];
  isPending: boolean;
  onSubmit: (payload: { groupName: string; agents: AgentRow[] }) => void;
}

export function AgentGroupForm({
  mode,
  initialGroupName,
  initialAgents,
  isPending,
  onSubmit,
}: Props) {
  const [groupName, setGroupName] = useState(initialGroupName);
  const [agents, setAgents] = useState<AgentRow[]>(initialAgents);

  const [agentName, setAgentName] = useState('');
  const [agentNumber, setAgentNumber] = useState('');
  const [agentType, setAgentType] = useState<AgentType | ''>('');

  const addAgent = () => {
    if (!agentName.trim() || !agentNumber.trim() || !agentType) {
      toast.error('Pick a name, number, and agent type.');
      return;
    }
    if (
      agents.some(
        (a) => a.agentNumber.trim() === agentNumber.trim(),
      )
    ) {
      toast.error('That agent number is already in the group.');
      return;
    }
    setAgents([
      ...agents,
      {
        agentName: agentName.trim(),
        agentNumber: agentNumber.trim(),
        agentType: agentType as AgentType,
      },
    ]);
    setAgentName('');
    setAgentNumber('');
    setAgentType('');
  };

  const removeAgent = (i: number) =>
    setAgents((prev) => prev.filter((_, idx) => idx !== i));

  const updateAgent = (i: number, patch: Partial<AgentRow>) =>
    setAgents((prev) =>
      prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)),
    );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupName.trim()) return toast.error('Give the group a name.');
    if (agents.length === 0)
      return toast.error('Add at least one agent before saving.');

    // In-place edits can temporarily produce blanks or duplicates while the
    // user is typing — re-validate the array as a whole at submit.
    const cleaned = agents.map((a) => ({
      agentName: a.agentName.trim(),
      agentNumber: a.agentNumber.trim(),
      agentType: a.agentType,
    }));
    for (const a of cleaned) {
      if (!a.agentName || !a.agentNumber)
        return toast.error('Every agent needs a name and a number.');
    }
    const seen = new Set<string>();
    for (const a of cleaned) {
      if (seen.has(a.agentNumber))
        return toast.error(
          `Duplicate agent number ${a.agentNumber} — each number can appear once.`,
        );
      seen.add(a.agentNumber);
    }
    onSubmit({ groupName: groupName.trim(), agents: cleaned });
  };

  return (
    <ScrollPage>
      <PageHeader
        title={mode === 'create' ? 'Create Agent Group' : 'Update Agent Group'}
        description="A pool of agents the inbound campaign can patch callers to, indexed by DTMF key."
        actions={
          <Button asChild variant="outline">
            <Link to="/group-agent-list">
              <ArrowLeft className="h-4 w-4" />
              Back to agent groups
            </Link>
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <Card>
          <CardHeader>
            <CardTitle>Group details</CardTitle>
            <CardDescription>
              The group name is shown on campaign-patch pickers.
            </CardDescription>
          </CardHeader>
          <CardContent className="max-w-md space-y-2">
            <Label htmlFor="group-name">
              Group name
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="group-name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              autoFocus={mode === 'create'}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Add an agent</CardTitle>
            <CardDescription>
              An agent is one phone number. A group can have many agents — calls
              hunt across them.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
              <div className="space-y-1.5">
                <Label htmlFor="agent-name">Agent name</Label>
                <Input
                  id="agent-name"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  placeholder="e.g. Reception"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="agent-number">Agent number</Label>
                <Input
                  id="agent-number"
                  value={agentNumber}
                  onChange={(e) => setAgentNumber(e.target.value)}
                  placeholder="10 digit mobile number"
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="agent-type">Agent type</Label>
                <Select
                  value={agentType}
                  onValueChange={(v) => setAgentType(v as AgentType)}
                >
                  <SelectTrigger id="agent-type">
                    <SelectValue placeholder="Pick a type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Normal Agent</SelectItem>
                    <SelectItem value="1">Call Center Agent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="button" onClick={addAgent}>
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>

            {agents.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-muted/20 py-6 text-center text-sm text-muted-foreground">
                No agents added yet.
              </div>
            ) : (
              <ol className="space-y-2">
                {agents.map((a, i) => {
                  const isCallCenter = a.agentType === '1';
                  const Icon = isCallCenter ? Users : Headset;
                  return (
                    <li
                      key={i}
                      className="grid grid-cols-1 gap-2 rounded-lg border border-border bg-card p-3 sm:grid-cols-[auto_1fr_1fr_160px_auto] sm:items-center"
                    >
                      <div
                        className={cn(
                          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                          isCallCenter
                            ? 'bg-sky-100 text-sky-700'
                            : 'bg-emerald-100 text-emerald-700',
                        )}
                        aria-label={AGENT_TYPE_LABEL[a.agentType]}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <Input
                        value={a.agentName}
                        onChange={(e) =>
                          updateAgent(i, { agentName: e.target.value })
                        }
                        placeholder="Agent name"
                        aria-label={`Agent ${i + 1} name`}
                      />
                      <Input
                        value={a.agentNumber}
                        onChange={(e) =>
                          updateAgent(i, { agentNumber: e.target.value })
                        }
                        placeholder="10 digit mobile number"
                        className="font-mono text-xs"
                        aria-label={`Agent ${i + 1} number`}
                      />
                      <Select
                        value={a.agentType}
                        onValueChange={(v) =>
                          updateAgent(i, { agentType: v as AgentType })
                        }
                      >
                        <SelectTrigger aria-label={`Agent ${i + 1} type`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Normal Agent</SelectItem>
                          <SelectItem value="1">Call Center Agent</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 justify-self-end text-destructive hover:text-destructive"
                        onClick={() => removeAgent(i)}
                        aria-label={`Remove ${a.agentName || `agent ${i + 1}`}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </li>
                  );
                })}
              </ol>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to="/group-agent-list">Cancel</Link>
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === 'create' ? 'Create group' : 'Save changes'}
          </Button>
        </div>
      </form>
    </ScrollPage>
  );
}
